import { isDevPreview } from '../boot/devPreview';
import { sb } from './supabase';
import { logActivity } from './repo';
import { emptyDocument, parseDocument, type CadDocument } from '../cad/model/document';
import { mm } from '../cad/model/units';
import { pt } from '../cad/model/geom';
import type { CadEntity } from '../cad/model/entity';
import { buildCatalogMap, makeBlockRef, resolveBlockDef } from '../cad/blocks/catalog';
import { FALLBACK_CATALOG } from '../cad/blocks/standardLibrary';
import type { CadOp, OpBatch } from '../cad/ops/operation';
import type { Drawing, DrawingVersion } from './types';

/**
 * 도면 본문 저장소 (작업지시서 P2-18 / P4-08)
 *
 * 저장 전략은 `고도화_제안_v2.md` 2.2절의 하이브리드를 따른다:
 *   · cad_version.document_json — 스냅샷 (자동 저장이 갱신)
 *   · cad_operation            — 연산 로그 (append-only)
 *
 * 자동 저장은 **연산 로그를 먼저 붙이고** 스냅샷을 갱신한다.
 * 순서가 중요하다 — 스냅샷 갱신이 실패해도 연산 로그가 남아 있으면 복구할 수 있지만,
 * 그 반대는 "무엇이 바뀌었는지"를 영원히 잃는다.
 */

export interface LoadedDrawing {
  drawing: Drawing;
  version: DrawingVersion;
  document: CadDocument;
  /** 이 버전을 편집할 수 있는가 (승인/배포본은 읽기 전용) */
  editable: boolean;
}

function fail(where: string, error: { message: string } | null): void {
  if (error) throw new Error(`${where}: ${error.message}`);
}

/* -------------------------------------------------------------------------- *
 * 열기
 * -------------------------------------------------------------------------- */

/**
 * 도면을 연다.
 * @param versionId 지정하면 그 버전을, 없으면 현재 브랜치의 최신 버전을 연다.
 */
export async function loadDrawing(
  drawingId: string,
  versionId?: string
): Promise<LoadedDrawing> {
  if (isDevPreview()) return devFixture(drawingId);

  const client = sb();

  const { data: d, error: e1 } = await client
    .from('cad_drawing')
    .select('*')
    .eq('id', drawingId)
    .maybeSingle();
  fail('도면 조회', e1);
  if (!d) throw new Error('도면을 찾을 수 없거나 접근 권한이 없습니다.');
  const drawing = d as Drawing;

  let q = client.from('cad_version').select('*').eq('drawing_id', drawingId);
  if (versionId) {
    q = q.eq('id', versionId);
  } else if (drawing.current_branch_id) {
    q = q.eq('branch_id', drawing.current_branch_id);
  }

  const { data: v, error: e2 } = await q
    .order('version_number', { ascending: false })
    .limit(1)
    .maybeSingle();
  fail('버전 조회', e2);
  if (!v) throw new Error('이 도면에는 아직 버전이 없습니다.');

  const version = v as DrawingVersion & { document_json: unknown; doc_path: string | null };

  // 큰 도면은 Storage 에 있다
  let raw: unknown = version.document_json;
  if (!raw && version.doc_path) {
    const { data: file, error: e3 } = await client.storage
      .from('cad-derived')
      .download(version.doc_path);
    fail('도면 본문 다운로드', e3);
    raw = JSON.parse(await file!.text());
  }

  await logActivity('drawing.open', {
    drawing: drawingId,
    version: version.id,
    project: drawing.project_id,
  });

  return {
    drawing,
    version,
    document: parseDocument(raw),
    // 승인·배포된 버전은 편집할 수 없다 (DB 트리거로도 이중 차단)
    editable: version.status !== 'approved' && version.status !== 'published',
  };
}

/* -------------------------------------------------------------------------- *
 * 자동 저장
 * -------------------------------------------------------------------------- */

export interface SaveResult {
  savedOps: number;
  at: string;
}

/**
 * 한 연산 행에 담을 최대 엔티티 수.
 *
 * ★ 왜 쪼개는가 — DXF 를 가져오면 "엔티티 15,691개를 추가" 라는 **연산 하나**가
 *   생긴다. 이걸 그대로 한 행에 넣으면 JSONB 2.5MB 짜리 행이 되고, 스냅샷
 *   2.5MB 와 합쳐 한 번 저장에 5MB 를 보낸다. 실제로 "저장 실패" 가 났고,
 *   재시도가 겹치면서 Supabase 프로젝트 자체가 먹통이 됐다.
 *
 *   add/delete 연산은 순서대로 이어 붙이면 결과가 같으므로, 여러 행으로
 *   나눠도 되감기·Undo 결과가 달라지지 않는다. 화면의 타임라인은 이미
 *   같은 명령끼리 한 칸으로 묶어 보여준다.
 */
const OP_ROW_ENTITY_LIMIT = 400;

/** 행 하나가 지나치게 커지지 않도록 연산을 여러 개로 나눈다 */
export function splitOpForStorage(op: CadOp): CadOp[] {
  if (op.kind === 'add' || op.kind === 'delete') {
    if (op.entities.length <= OP_ROW_ENTITY_LIMIT) return [op];
    const out: CadOp[] = [];
    for (let i = 0; i < op.entities.length; i += OP_ROW_ENTITY_LIMIT) {
      out.push({ ...op, entities: op.entities.slice(i, i + OP_ROW_ENTITY_LIMIT) });
    }
    return out;
  }
  if (op.kind === 'update') {
    if (op.after.length <= OP_ROW_ENTITY_LIMIT) return [op];
    const out: CadOp[] = [];
    for (let i = 0; i < op.after.length; i += OP_ROW_ENTITY_LIMIT) {
      // before/after 는 짝이 맞아야 역연산(Undo)이 성립한다 — 같은 구간으로 자른다
      out.push({
        ...op,
        before: op.before.slice(i, i + OP_ROW_ENTITY_LIMIT),
        after: op.after.slice(i, i + OP_ROW_ENTITY_LIMIT),
      });
    }
    return out;
  }
  return [op]; // layer / meta 는 작다
}

/** 이 크기를 넘는 스냅샷은 DB 행이 아니라 Storage 에 둔다 */
const SNAPSHOT_INLINE_LIMIT = 512 * 1024;

/** 한 번에 보낼 연산 행 수 */
const OP_INSERT_CHUNK = 200;

/**
 * 이미 연산 로그에 써 넣은 배치 id.
 *
 * ★ 왜 필요한가 — 저장은 "연산 로그 insert" 다음 "스냅샷 update" 두 단계인데
 *   트랜잭션이 아니다. 앞이 성공하고 뒤가 실패하면 호출자가 pending 을
 *   되돌려 **같은 연산을 통째로 다시 insert** 한다. 실패가 반복되면 로그가
 *   눈덩이처럼 불어나 DB 를 갉아먹는다(연산 하나가 수 MB 였다).
 *   여기서 한 번 기록한 배치를 기억해 두고 다시 쓰지 않는다.
 */
const loggedBatchIds = new Set<string>();
const LOGGED_ID_CAP = 5_000;

function rememberLogged(ids: string[]): void {
  for (const id of ids) loggedBatchIds.add(id);
  if (loggedBatchIds.size > LOGGED_ID_CAP) {
    // 오래된 것부터 버린다 — Set 은 삽입 순서를 지킨다
    const excess = loggedBatchIds.size - LOGGED_ID_CAP;
    let n = 0;
    for (const id of loggedBatchIds) {
      loggedBatchIds.delete(id);
      if (++n >= excess) break;
    }
  }
}

/**
 * 연산 로그를 붙이고 스냅샷을 갱신한다.
 * 실패하면 던진다 — 호출자가 pending 을 되돌려 유실 0 을 보장한다.
 */
export async function saveDocument(
  versionId: string,
  doc: CadDocument,
  batches: OpBatch[]
): Promise<SaveResult> {
  if (isDevPreview()) {
    return { savedOps: batches.length, at: new Date().toISOString() };
  }

  const client = sb();

  // 1) 연산 로그 먼저 — 무엇이 바뀌었는지를 잃지 않기 위해
  //    (스냅샷 저장이 실패해 재시도로 다시 들어온 배치는 건너뛴다)
  const unlogged = batches.filter((b) => !loggedBatchIds.has(b.id));
  if (unlogged.length > 0) {
    const { data: last, error: eSeq } = await client
      .from('cad_operation')
      .select('seq')
      .eq('version_id', versionId)
      .order('seq', { ascending: false })
      .limit(1)
      .maybeSingle();
    fail('연산 순번 조회', eSeq);

    let seq = ((last as { seq: number } | null)?.seq ?? 0) + 1;

    const rows = unlogged.flatMap((b) =>
      b.ops.flatMap(splitOpForStorage).map((op) => ({
        version_id: versionId,
        seq: seq++,
        kind: op.kind,
        command: op.command,
        entity_ids: opEntityIds(op),
        before: 'before' in op ? op.before : null,
        after: 'after' in op ? op.after : 'entities' in op ? op.entities : null,
      }))
    );

    // 한 요청에 몰아 보내면 다시 거대한 payload 가 된다 — 나눠 보낸다
    for (let i = 0; i < rows.length; i += OP_INSERT_CHUNK) {
      const { error } = await client
        .from('cad_operation')
        .insert(rows.slice(i, i + OP_INSERT_CHUNK));
      fail('연산 로그 저장', error);
    }
    rememberLogged(unlogged.map((b) => b.id));
  }

  // 2) 스냅샷 갱신
  //
  // ★ 큰 도면은 DB 행에 넣지 않고 Storage 로 뺀다. cad_version.doc_path 는
  //   원래 이 용도로 설계돼 있고 loadDrawing() 도 이미 그 경로를 읽는데,
  //   저장 쪽만 구현이 빠져 있어서 2.5MB 짜리 document_json 을 10초마다
  //   통째로 밀어 넣고 있었다. 그게 저장 실패의 직접 원인이다.
  const json = JSON.stringify(doc);
  const bytes = new TextEncoder().encode(json).length;

  if (bytes > SNAPSHOT_INLINE_LIMIT) {
    const owner = await versionOwner(client, versionId);
    // 버전마다 고정 경로 — 덮어쓰기라서 저장할 때마다 파일이 쌓이지 않는다
    const docPath = `${owner.projectId}/${owner.drawingId}/${versionId}/derived/document.json`;

    const up = await client.storage
      .from('cad-derived')
      .upload(docPath, new Blob([json], { type: 'application/json' }), {
        contentType: 'application/json',
        upsert: true,
      });
    if (up.error) throw new Error(`도면 본문 업로드 실패: ${up.error.message}`);

    const { error } = await client
      .from('cad_version')
      .update({
        // Storage 에 있으므로 행에는 넣지 않는다 (loadDrawing 이 doc_path 를 본다)
        document_json: null,
        doc_path: docPath,
        entity_count: doc.entities.length,
      })
      .eq('id', versionId);
    fail('도면 저장', error);
  } else {
    const { error } = await client
      .from('cad_version')
      .update({
        document_json: doc,
        doc_path: null,
        entity_count: doc.entities.length,
      })
      .eq('id', versionId);
    fail('도면 저장', error);
  }

  return { savedOps: batches.length, at: new Date().toISOString() };
}

/** Storage 경로의 첫 조각은 project_id 여야 한다 (0006_cad_storage.sql 정책) */
async function versionOwner(
  client: ReturnType<typeof sb>,
  versionId: string
): Promise<{ drawingId: string; projectId: string }> {
  const { data: v, error: e1 } = await client
    .from('cad_version')
    .select('drawing_id')
    .eq('id', versionId)
    .single();
  fail('버전 조회', e1);

  const drawingId = (v as { drawing_id: string }).drawing_id;
  const { data: d, error: e2 } = await client
    .from('cad_drawing')
    .select('project_id')
    .eq('id', drawingId)
    .single();
  fail('도면 조회', e2);

  return { drawingId, projectId: (d as { project_id: string }).project_id };
}

function opEntityIds(op: OpBatch['ops'][number]): string[] {
  if ('entities' in op) return op.entities.map((e) => e.id);
  if ('after' in op && Array.isArray(op.after)) {
    return (op.after as CadEntity[]).map((e) => e.id);
  }
  return [];
}

/* -------------------------------------------------------------------------- *
 * 개발 미리보기용 샘플 도면
 * -------------------------------------------------------------------------- */

/**
 * DB 없이 편집기를 열 수 있게 하는 샘플 도면.
 * 마트 1층 일부를 흉내 낸다 — 외벽, 통로, 곤돌라 몇 줄, 계산대.
 */
function devFixture(drawingId: string): LoadedDrawing {
  const doc = emptyDocument('mm');
  let n = 0;
  const id = () => `fx${n++}`;

  const wall = (ax: number, ay: number, bx: number, by: number): CadEntity => ({
    id: id(),
    type: 'line',
    layer: 'A-WALL',
    a: pt(mm(ax), mm(ay)),
    b: pt(mm(bx), mm(by)),
  });

  // 외벽 30m × 20m
  doc.entities.push(
    wall(0, 0, 30000, 0),
    wall(30000, 0, 30000, 20000),
    wall(30000, 20000, 0, 20000),
    wall(0, 20000, 0, 0)
  );

  /*
   * 집기는 **블록 참조**로 넣는다. 선으로 그려 넣으면 겉보기는 같지만
   * 물량 산출이 아무것도 못 센다 — 이 제품의 핵심 기능이 샘플에서
   * 동작하지 않는 상태가 된다.
   */
  const catalog = buildCatalogMap(FALLBACK_CATALOG);
  const place = (code: string, x: number, y: number, params: Record<string, number> = {}) => {
    const block = catalog.get(code);
    if (!block) return;
    const def = resolveBlockDef(block, params);
    if (!doc.blocks.some((b) => b.code === def.code)) doc.blocks.push(def);

    const ref = makeBlockRef(block, { at: pt(mm(x), mm(y)), params });
    ref.id = id();
    ref.blockCode = def.code;
    doc.entities.push(ref);
  };

  /*
   * 곤돌라 4열. 한 열은 900 폭 × 5베이 = 4500mm 길이, 깊이 900mm.
   * 열 간격 3300 = 깊이 900 + 통로 2400(주통로 기준).
   * ★ 간격을 좁히면 검증 패널(P5-02)이 바로 잡는다 — 샘플이 규칙을 어기면 안 된다.
   */
  for (let row = 0; row < 4; row++) {
    place('GONDOLA', 4000, 4000 + row * 3300, {
      width: 900,
      depth: 900,
      bays: 5,
      shelfLevels: 5,
    });
  }

  // 계산대 4대
  for (let i = 0; i < 4; i++) {
    place('POS-COUNTER', 3000 + i * 2600, 16000, { width: 1800, depth: 900 });
  }

  // 냉장 쇼케이스 (벽면)
  place('SHOWCASE', 25000, 4000, { width: 3750, depth: 900, shelfLevels: 4 });

  // 소방 설비 — 이격 검증(P5-03)이 실제로 걸리는지 보려면 샘플에도 있어야 한다
  place('FIRE-EXT', 1000, 1000);
  place('EMERGENCY-EXIT', 28000, 19500);

  doc.entities.push({
    id: id(),
    type: 'text',
    layer: 'A-DIM',
    at: pt(mm(1000), mm(18500)),
    text: '개발 미리보기 샘플 도면 — 실제 데이터가 아닙니다',
    height: mm(400),
    rotation: 0,
    align: 'left',
  });

  const now = new Date().toISOString();
  return {
    drawing: {
      id: drawingId,
      project_id: 'p-0001',
      name: '1F 리뉴얼 A안 (샘플)',
      drawing_type: 'floor_plan',
      floor_name: '1F',
      zone_tags: ['신선', '행사'],
      unit: 'mm',
      current_branch_id: 'b-dev',
      published_version_id: null,
      thumbnail_path: null,
      created_by: 'dev@localhost',
      created_at: now,
      updated_at: now,
    },
    version: {
      id: 'v-dev',
      drawing_id: drawingId,
      branch_id: 'b-dev',
      version_number: 1,
      name: '샘플',
      change_summary: null,
      status: 'draft',
      entity_count: doc.entities.length,
      created_by: 'dev@localhost',
      created_at: now,
    },
    document: doc,
    editable: true,
  };
}
